/**
 * Column conventions, in one place so no table can quietly break one.
 *
 * The rules they encode come from database/README.md:
 *
 * - **Instants are UTC epoch milliseconds** in an `integer` column. SQLite has no
 *   timezone-aware type, and storing local time loses the offset.
 * - **Calendar dates are `TEXT` in `YYYY-MM-DD`** and are never timezone-converted. An event
 *   that starts on the 14th starts on the 14th regardless of who is looking.
 * - **Money is integer cents with an explicit currency.** Never a float.
 * - **Every mutable row records who wrote it**, and the actor is not always a user.
 *
 * Each helper is a function rather than a shared object because Drizzle column builders carry
 * per-table state; spreading one object into two tables aliases them.
 */

import { integer, text } from "drizzle-orm/sqlite-core";

/**
 * The actor types from `packages/domain`. A participant holding a secure token and a staff
 * member registering on their behalf are different facts, and the interface has to be able to
 * say which one happened — "Registered by participant" versus "Registered by Jane Smith,
 * Registrar".
 */
export const ACTOR_TYPES = ["user", "participant", "staff_assisted", "import", "system"] as const;

export type ActorType = (typeof ACTOR_TYPES)[number];

/** ULID primary key. Client-generated where a row can originate offline, so retries are idempotent. */
export const id = () => text("id").primaryKey();

/** A ULID foreign-key column. */
export const ref = (name: string) => text(name);

/** UTC epoch milliseconds. */
export const instant = (name: string) => integer(name);

/** A calendar date, `YYYY-MM-DD`. Not an instant — see the module comment. */
export const calendarDate = (name: string) => text(name);

/** Integer cents. Paired with a `currency` column wherever it appears. */
export const cents = (name: string) => integer(name);

export const currency = () => text("currency").notNull().default("CAD");

/** Arbitrary JSON, stored as text and parsed by the domain layer against a Zod schema. */
export const json = (name: string) => text(name, { mode: "json" });

/** A boolean. SQLite has none; Drizzle maps 0/1 for us so callers never see the integer. */
export const flag = (name: string) => integer(name, { mode: "boolean" });

/**
 * Creation and modification stamps with actor attribution. Written by the repository layer, not
 * by callers, so it is not possible to write a row without recording who caused it.
 */
export const audited = () => ({
  createdAt: instant("created_at").notNull(),
  createdByActorType: text("created_by_actor_type", { enum: ACTOR_TYPES }).notNull(),
  createdByActorId: text("created_by_actor_id"),
  updatedAt: instant("updated_at").notNull(),
  updatedByActorType: text("updated_by_actor_type", { enum: ACTOR_TYPES }).notNull(),
  updatedByActorId: text("updated_by_actor_id"),
});

/**
 * Soft deletion. Present on everything a result or a payout could reference — nothing that
 * money or a published result depends on is ever hard-deleted.
 */
export const archivable = () => ({
  archivedAt: instant("archived_at"),
});

/** The tenant column. Its presence on a table is what makes the table tenant-scoped (D5). */
export const communityId = () => ref("community_id").notNull();

export const sortOrder = () => integer("sort_order").notNull().default(0);
